import { useTranslation } from 'react-i18next';

import { Form, FormGenerator } from '@/shared/ui';
import { Dialog } from '@/shared/ui/Dialog';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { Button } from '@/shared/ui/lib/Button';

import { disableTwoFaSchema } from '../model/schemas';
import type {
  DisableTwoFaDialogProps,
  DisableTwoFaFormDataType,
} from '../model/types';
import { useUpdateAccountSettings } from '../model/useUpdateAccountSettings';

const DISABLE_TWO_FA_FORM = 'disableTwoFaForm';
const DISABLE_TWO_FA_FORM_DIALOG = 'disableTwoFaFormDialog';

export const DisableTwoFaDialog = ({ open, onClose }: DisableTwoFaDialogProps) => {
  const { t } = useTranslation();
  const { updateAccountSettings, isPending } = useUpdateAccountSettings();

  const formScheme = [
    {
      name: 'currentPassword',
      label: t('common:inputs.currentPassword'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.password'),
      },
    },
  ];

  const handleSubmit = ({ currentPassword }: DisableTwoFaFormDataType) => {
    updateAccountSettings(
      { twofa_is_active: false, current_password: currentPassword },
      // Close only on success — on error the notification fires and the user can retry.
      onClose
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      id={DISABLE_TWO_FA_FORM_DIALOG}
      title={t('settings:account.2fa.disableTitle')}
      leftButtonSlot={
        <Button onClick={onClose} variant="outlined" disabled={isPending}>
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          type="submit"
          form={DISABLE_TWO_FA_FORM}
          variant="contained"
          color="primary"
          disabled={isPending}
        >
          {t('settings:account.2fa.disable')}
        </Button>
      }
    >
      <Form
        id={DISABLE_TWO_FA_FORM}
        schema={disableTwoFaSchema(t)}
        onSubmit={handleSubmit}
        defaultValues={{ currentPassword: '' }}
        mode="onChange"
        sx={{ maxWidth: '400px' }}
      >
        <FormGenerator formScheme={formScheme} />
      </Form>
    </Dialog>
  );
};
